import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import {
  switchMap,
  BehaviorSubject,
  debounceTime,
  finalize,
  take,
} from 'rxjs';
import { HttpService } from 'src/app/core/services/api/http.service';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NewContractService } from '../../../modules/contracts/components/create-contract/new-contract.service';
import { ThirdPartiesService } from 'src/app/modules/third-parties-layout/third-parties.service';
import { CONTRACT_STATUSES } from '../../../modules/contracts/consts/contract-statuses.const';
import { FilterOption, Option } from '../../models/option.interface';
import { ContractsApiService } from '../../../core/services/api/contracts-api.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApiResponse } from '../../models/api-response.interface';
import { API_VERSIONS } from '../../consts/api-versions.const';
import { saveBlobAsFile } from '../../utils/save-blob-as-file.util';
import { UsersGroup } from '../../enums/users-group.enum';
import { NotificationsService } from 'angular2-notifications';

@UntilDestroy()
@Component({
  selector: 'app-list-contract',
  templateUrl: './list-contract.component.html',
  styleUrls: [
    './list-contract.component.css',
    '../../../modules/contracts/contracts.component.css',
  ],
})
export class ListContractComponent implements OnInit {
  @Input() isExportable: boolean = false;

  usersSuccessCallback = (res: any[]) =>
    res?.map((i) => ({
      ...i,
      company_name: i?.third_party_info?.name
        ? i?.third_party_info?.name
        : i?.first_name + ' ' + i?.last_name,
    }));

  creationSourcesSuccessCallback = (res: any[]) =>
    (res || []).map((source: any) => ({ id: source.value, name: source.label }));

  FILTER_OPTIONS: FilterOption[] = [
    {
      name: 'Filter by creation method',
      key: 'filter_by_creation_source',
      isAutocomplete: true,
      autocompleteUrl: '/contracts/creation-sources',
      optionLabel: 'name',
      optionValue: 'id',
      placeholder: 'Select creation method',
      options: [],
      successCallback: this.creationSourcesSuccessCallback,
    },
    {
      name: 'Filter by product',
      key: 'filter_by_product',
      isAutocomplete: true,
      isPaginated: true,
      autocompleteUrl: `/${API_VERSIONS.PRODUCTS}/products?include_atlantic_products=true`,
      optionLabel: 'label',
      optionValue: 'id',
      searchCategory: 'search_by_label',
      placeholder: 'Search for product',
      options: [],
    },
    {
      name: 'Filter by Plans',
      key: 'filter_by_plan',
      isAutocomplete: true,
      isPaginated: true,
      autocompleteUrl: `/${API_VERSIONS.CONTRACTS}/contracts/plans/paging?sort=name&with_statistics=1&include_atlantic_products=true`,
      optionLabel: 'name',
      optionValue: 'id',
      searchCategory: 'search_by_name',
      placeholder: 'Search for plan',
      options: [],
    },
    {
      name: 'Filter by type',
      key: 'filter_by_type',
      isAutocomplete: true,
      autocompleteUrl: `/${API_VERSIONS.TYPES}/types?model=contract&filter_by_active=1`,
      optionLabel: 'name',
      optionValue: 'id',
      placeholder: 'Search for type',
      searchCategory: 'search_by_name',
      options: [],
    },
    {
      name: 'Filter by status',
      key: 'filter_by_status',
      options: [
        {
          name: 'New',
          id: 'new',
        },
        {
          name: 'Approved',
          id: 'approved',
        },
        {
          name: 'Draft',
          id: 'draft',
        },
        {
          name: 'Pending',
          id: 'pending',
        },
        {
          name: 'Active',
          id: 'active',
        },
        {
          name: 'Cancelled',
          id: 'cancelled',
        },
        {
          name: 'Expired',
          id: 'expired',
        },
        {
          name: 'Void',
          id: 'void',
        },
        {
          name: 'Pending Funding',
          id: 'pending funding',
        },
        {
          name: 'Pending Cancellation Approval',
          id: 'pending cancellation approval',
        },
        {
          name: 'Past Due',
          id: 'past due',
        },
        {
          name: 'Terminated',
          id: 'terminated',
        },
      ],
    },
    {
      name: 'Filter by dealer',
      key: 'filter_by_dealers',
      isAutocomplete: true,
      autocompleteUrl: `/${API_VERSIONS.USERS}/users/paging?filter_by_group=5`,
      optionLabel: ['company_name', 'third_party_info.internal_code'],
      optionValue: 'id',
      searchCategory: 'search',
      placeholder: 'Search for dealer',
      options: [],
      successCallback: this.usersSuccessCallback,
    },
    {
      name: 'Filter by dealer group',
      key: 'filter_by_dealer_groups',
      isAutocomplete: true,
      autocompleteUrl: `/${API_VERSIONS.USERS}/users/paging?filter_by_group=${UsersGroup.DealerGroups}`,
      optionLabel: 'company_name',
      optionValue: 'id',
      searchCategory: 'search',
      placeholder: 'Search for dealer group',
      options: [],
      successCallback: this.usersSuccessCallback,
    },
    {
      name: 'Filter by agent',
      key: 'filter_by_agents',
      isAutocomplete: true,
      autocompleteUrl: `/${API_VERSIONS.USERS}/users/paging?filter_by_group=2`,
      optionLabel: 'company_name',
      optionValue: 'id',
      searchCategory: 'search',
      placeholder: 'Search for agent',
      options: [],
      successCallback: this.usersSuccessCallback,
    },
    {
      name: 'Filter by broker',
      key: 'filter_by_brokers',
      isAutocomplete: true,
      autocompleteUrl: `/${API_VERSIONS.USERS}/users/paging?filter_by_group=3`,
      optionLabel: 'company_name',
      optionValue: 'id',
      searchCategory: 'search',
      placeholder: 'Search for broker',
      options: [],
      successCallback: this.usersSuccessCallback,
    },
    {
      name: 'Filter by seller state',
      key: 'filter_by_creator_state',
      isAutocomplete: true,
      autocompleteUrl: `/states/paging`,
      optionLabel: 'state',
      optionValue: 'id',
      searchCategory: 'global_search',
      placeholder: 'Search for state',
      options: [],
    },
    {
      name: 'Filter by Effective Date',
      key: 'filter_by_start_date_range',
      isDateRange: true,
      placeholder: 'Select Range',
      options: [],
    },
    {
      name: 'Filter by Sale Date',
      key: 'filter_by_sale_date',
      isDateRange: true,
      placeholder: 'Select Range',
      tooltip:
        'Contracts with no sale date recorded are matched on the date they were created.',
      options: [],
    },
    {
      name: 'Sale Date From',
      key: 'filter_by_sale_date_from',
      isDate: true,
      placeholder: 'Select start date',
      helperText: 'Contracts with no sale date recorded are matched on the date they were created.',
      options: [],
    },
    {
      name: 'Sale Date To',
      key: 'filter_by_sale_date_to',
      isDate: true,
      placeholder: 'Select end date',
      options: [],
    },
    {
      name: 'Fund Status',
      key: 'filter_by_fund_status',
      options: [
        {
          name: 'Fully Funded',
          id: 'Fully Funded',
        },
        {
          name: 'Not Funded',
          id: 'Not Funded',
        },
        {
          name: 'Over Funded',
          id: 'Over Funded',
        },
        {
          name: 'Under Funded',
          id: 'Under Funded',
        },
      ],
    },
  ];

  @Input() productId?: number;
  @Input() vehicleId?: number;
  @Input() dealerId?: number;

  isLoadingStatus: boolean = false;
  Loading: any = ['', '', '', '', '', '', '', ''];
  //pagination
  currentPage: number = 1;
  totalItems: number = 100;
  queryParams: any;
  selectCategory: any;
  categories: any = [{ name: 'option1' }, { name: 'option2' }];

  // selectSort:any;
  isMobile: boolean = window.innerWidth < 768 ? true : false;

  //List Api
  filterParams: any = {};
  hiddenFilterParams: any = {};
  isLoading: boolean = false;
  contractData: any[] = [];
  serverErrorFetch: string | null = '';

  //Search
  searchKeyword = '';
  searchInput = new BehaviorSubject('');
  selectedSort: any = '';
  selectedFilter: any = '';
  search_by_options: Option[] = [
    { name: 'Contract Number', key: 'search_by_contract_number' },
    { name: 'Contract Type', key: 'search_by_contract_type' },
    { name: 'Reference Code', key: 'search_by_ref_code' },
    { name: 'Customer Name', key: 'search_by_customer_name' },
    { name: 'Phone Number', key: 'search_by_phone_number' },
    { name: 'Plan Code', key: 'search_by_plan_code' },
    { name: 'Start Date', key: 'search_by_start_date' },
    { name: 'Expiry Date', key: 'search_by_expiry_date' },
    { name: 'Vehicle VIN', key: 'search_by_vin_number' },
    { name: 'Address', key: 'search_by_address' },
    { name: 'Batch Number', key: 'search_by_batch_number' },
  ];
  search_by: any[] = [];

  //Sort
  sorts: Option[] = [
    { name: 'Default', key: '-id' },
    {
      name: 'Alphabetical Order Increasing',
      key: '-reference_code',
    },
    {
      name: 'Alphabetical Order Decreasing',
      key: 'reference_code',
    },
    { name: 'Newest Created First', key: '-id' },
    { name: 'Oldest Created First', key: 'id' },
    { name: 'Newest Edited', key: '-updated_at' },
    { name: 'Latest Edited', key: 'updated_at' },
    { name: 'Creation Method A–Z', key: 'creation_source' },
    { name: 'Creation Method Z–A', key: '-creation_source' },
  ];
  productsName: any;
  selectSortQuote: any = {};
  viewFrom: any;
  viewTo: any;
  whoEntered: any;
  readonly brokerContractTabs = [
    { label: 'Paid', value: 'paid' },
    { label: 'Pending Funding', value: 'pending_funding' },
    { label: 'Void', value: 'void' },
  ];
  brokerContractTabIndex = 0;
  brokerContractBasicParams?: Record<string, string>;
  brokerContractPageSize = 10;
  userId: any;
  popup: any;
  tags: any[] = [];
  selectTag: any = {};
  isLoadingTag: boolean = false;
  types: any[] = [];
  selectType: any = {};
  selectProduct: any = {};
  selectedStatus?: string;
  isLoadingType: boolean = false;
  isLoadingDealers: boolean = false;
  isLoadingAgents: boolean = false;
  isLoadingBrokers: boolean = false;
  isExportLoading: boolean = false;
  isFiltering: boolean = false;
  selectedContractIds: number[] = [];
  showBulkChangeStatusModal = false;
  @ViewChild('filterClaims') filterClaims!: ElementRef;
  //Show Columns
  tableName: string = 'Contract';
  tableOptions: any = [
    'Contract Number',
    'Product Name',
    'Dealer',
    'Customer Name',
    'Phone',
    'Contract Date',
    'Contract Exp. Date',
    'Total Balance',
    'Total Remit.',
    'Retail Cost',
    'Status',
    'Creation Method',
  ];
  tableSelected: any = {};
  protected readonly CONTRACT_STATUSES = CONTRACT_STATUSES;
  constructor(
    private router: Router,
    private _api: HttpService,
    private activatedRouter: ActivatedRoute,
    private errHandle: ErrorHandlingService,
    private contract: NewContractService,
    private _thirdParties: ThirdPartiesService,
    private contractsApiService: ContractsApiService,
    private permissionsService: PermissionsService,
    private notifications: NotificationsService
  ) { }

  get canUpdateContractStatus(): boolean {
    return this.permissionsService.hasPermission('update-status_contract');
  }

  get canShowBulkStatusChange(): boolean {
    return (
      this.canUpdateContractStatus &&
      this.router.url.includes('/dashboard/contracts/contract-list')
    );
  }

  get isPortalContractsPage(): boolean {
    return ['agent-contracts', 'broker-contracts'].includes(this.whoEntered);
  }

  get isBrokerContractsPage(): boolean {
    return this.whoEntered === 'broker-contracts';
  }

  onBrokerContractTabChange(event: { index: number }): void {
    this.router.navigate([], {
      queryParams: {
        page: 1,
        filter_by_paid_status: this.brokerContractTabs[event.index].value,
      },
      queryParamsHandling: 'merge',
    });
  }

  onBrokerContractPagination(event: { page: number; per_page: number }): void {
    this.router.navigate([], {
      queryParams: { page: event.page, length: event.per_page },
      queryParamsHandling: 'merge',
    });
  }

  private applyBrokerContractTab(params: Params): void {
    if (!this.isBrokerContractsPage) {
      return;
    }
    this.currentPage = Number(params['page']) || 1;
    this.brokerContractPageSize = Number(params['length']) || 10;
    const index = this.brokerContractTabs.findIndex(
      (tab) => tab.value === params['filter_by_paid_status']
    );
    this.brokerContractTabIndex = index < 0 ? 0 : index;
    params['filter_by_paid_status'] = this.brokerContractTabs[this.brokerContractTabIndex].value;
    if (this.brokerContractBasicParams?.['filter_by_paid_status'] !== params['filter_by_paid_status']) {
      this.brokerContractBasicParams = {
        filter_by_paid_status: params['filter_by_paid_status'],
      };
    }
  }

  ngOnInit(): void {
    this.whoEntered = this.activatedRouter.snapshot.data['whoEntered'];
    this.activatedRouter.params.subscribe((param) => {
      this._thirdParties.invokeUpdatedThirdPartiesId(param['id']);
      this.userId = param['id'];
    });
    if (this.dealerId) {
      this.FILTER_OPTIONS = this.FILTER_OPTIONS.map((option) =>
        option.key === 'filter_by_dealers' ? null : option
      ).filter((option) => !!option) as FilterOption[];
    }
    this.getContracts();
  }

  getContractProducts(products: []) {
    const productNames = products.map((product: any) => {
      return product.label ? product.label.toUpperCase() : '';
    });
    if (!productNames.length) {
      return 'No products found';
    }
    return productNames.join(' , ');
  }
  viewContractDetails(id: number) {
    if (this.whoEntered == 'agent-contracts') {
      this.router.navigate([
        `/third-parties/agent-dashboard/contract-details/${id}`,
      ]);
    } else if (this.whoEntered == 'broker-contracts') {
      this.router.navigate([
        `/third-parties/broker-dashboard/contract-details/${id}`,
      ]);
    } else if (this.whoEntered == 'agent') {
      this.router.navigate([
        `/third-parties/agent-dashboard/dealerships/contract-details/${id}`,
      ]);
    } else if (this.whoEntered == 'broker') {
      this.router.navigate([
        `/third-parties/broker-dashboard/agency/contract-details/${id}`,
      ]);
    } else {
      this.router.navigate([`/dashboard/contracts/contract-details/${id}`]);
    }
  }

  previewContractDocument(id: number): void {
    this.contractsApiService.previewContractDocument(id).pipe(take(1)).subscribe({
      next: (response: any) => {
        const blob: Blob = response?.body;
        if (!blob) {
          return;
        }
        if (blob.type.includes('json')) {
          this.showPreviewMessage(blob);
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      },
      error: (error: any) => {
        if (error?.error instanceof Blob && error.error.type.includes('json')) {
          this.showPreviewMessage(error.error);
          return;
        }
        this.errHandle.errorHandling(error);
      },
    });
  }

  private showPreviewMessage(blob: Blob): void {
    blob.text().then((text) => {
      try {
        const payload = JSON.parse(text);
        this.notifications.info('', payload?.message || 'Contract document is not available.');
      } catch {
        this.notifications.info('', 'Contract document is not available.');
      }
    });
  }
  setEditingContractState(id: number) {
    this.router.navigate([`/dashboard/contracts/edit-contract/${id}`]);
    this.contract.isEditingContract = true;
    this.contract.contractId = id;
  }

  // getAllContract() {

  // }

  successCallBackForGettypes(res: any) {
    this.contractData = res?.data ? res?.data : [];
    this.contractData.forEach((contract) => {
      contract['dealer_name'] = contract?.dealer ? contract?.dealer?.name : '-';
    });

    this.viewFrom = res?.meta?.from;
    this.viewTo = res?.meta?.to;
    this.totalItems = res?.meta?.total;
    this.currentPage = res.meta.current_page;
    this.isLoading = false;
    this.serverErrorFetch = null;
  }
  errCallbackForGettypes(err: any) {
    // use error handling service
    this.serverErrorFetch =
      err?.error?.message || 'Something wrong happened, please try again later';
    this.isLoading = false;
  }

  //sort
  onSort() {
    this.router.navigate([], {
      queryParams: { page: 1, sort: this.selectSortQuote.name },
      queryParamsHandling: 'merge',
    });
  }

  stopProp(event: any) {
    event.stopPropagation();
  }

  openCloseOptionsList(indexOption: number) {
    const optionsList = document.querySelectorAll('.filter .option-list');
    const iconsOptionList: any = document.querySelectorAll(
      '.filter .more-options img'
    );
    optionsList.forEach((option: any, index: number) => {
      if (indexOption == index) {
        if (option.classList.contains('show')) {
          iconsOptionList[index].src = './assets/images/close-filter.svg';
        } else {
          iconsOptionList[index].src = './assets/images/open-filter.svg';
        }
        option.classList.toggle('show');
      }
    });
  }
  openFilterClaims(e: any) {
    this.popup = e;
    if (!this.isFiltering) {
      e.stopPropagation();
      this.filterClaims.nativeElement.classList.toggle('show');
    }
  }

  paginate(e: any) {
    this.currentPage = e;
    this.router.navigate([], {
      queryParams: { page: this.currentPage },
      queryParamsHandling: 'merge',
    });
  }

  handleOutput(output: string) {
    this.tableSelected = output;
  }

  isSelected(contractId: number): boolean {
    return this.selectedContractIds.includes(contractId);
  }

  onRowSelectionChange(contractId: number, checked: boolean): void {
    if (!contractId) {
      return;
    }
    if (checked) {
      if (!this.isSelected(contractId)) {
        this.selectedContractIds = [...this.selectedContractIds, contractId];
      }
      return;
    }
    this.selectedContractIds = this.selectedContractIds.filter(
      (id) => id !== contractId
    );
  }

  getSelectableContractIds(): number[] {
    return this.contractData
      .map((contract) => Number(contract?.id))
      .filter((id) => !Number.isNaN(id));
  }

  areAllSelected(): boolean {
    const ids = this.getSelectableContractIds();
    return (
      ids.length > 0 && ids.every((id) => this.selectedContractIds.includes(id))
    );
  }

  toggleAllSelections(checked: boolean): void {
    if (!checked) {
      this.clearSelection();
      return;
    }
    this.selectedContractIds = this.getSelectableContractIds();
  }

  clearSelection(): void {
    this.selectedContractIds = [];
  }

  openBulkChangeStatusModal(): void {
    if (!this.selectedContractIds.length) {
      return;
    }
    this.showBulkChangeStatusModal = true;
  }

  onBulkChangeStatusModalVisibilityChange(visible: boolean): void {
    this.showBulkChangeStatusModal = visible;
  }

  onBulkStatusChangeConfirmed(): void {
    this.showBulkChangeStatusModal = false;
    this.clearSelection();
    this.refreshContracts();
  }

  private refreshContracts(): void {
    const params = { ...this.activatedRouter.snapshot.queryParams };
    this.applyBrokerContractTab(params);
    if (this.productId) {
      params['filter_by_product'] = this.productId;
    }
    if (this.vehicleId) {
      params['filter_by_vehicle'] = this.vehicleId;
    }
    if (this.dealerId) {
      params['filter_by_dealers'] = this.dealerId;
    }
    if (!params['sort']) {
      params['sort'] = '-id';
    }

    this.isLoading = true;
    this.contractsApiService
      .getAll(params)
      .pipe(take(1))
      .subscribe({
        next: (response: ApiResponse) => {
          this.totalItems = response.meta?.total ?? 0;
          this.contractData = response.data || [];
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  private getContracts(): void {
    this.activatedRouter.queryParams
      .pipe(
        untilDestroyed(this),
        switchMap((params: Params) => {
          this.isLoading = true;
          const payload = {
            ...params,
          };
          this.applyBrokerContractTab(payload);
          if (this.productId) {
            payload['filter_by_product'] = this.productId;
          }
          if (this.vehicleId) {
            payload['filter_by_vehicle'] = this.vehicleId;
          }
          if (this.dealerId) {
            payload['filter_by_dealers'] = this.dealerId;
          }
          if (!payload['sort']) {
            payload['sort'] = '-id';
          }
          return this.contractsApiService.getAll(payload);
        })
      )
      .subscribe({
        next: (response: ApiResponse) => {
          this.totalItems = response.meta?.total ?? 0;
          this.contractData = response.data || [];
          this.isLoading = false;
          this.clearSelection();
        },
        error: () => (this.isLoading = false),
      });
  }

  exportContracts(): void {
    const params: any = {
      filter_by_creator: this.userId,
    };

    this.isExportLoading = true;
    this._api
      .getReq('/product-contracts/export', {
        responseType: 'blob',
        observe: 'response',
        params,
      })
      .pipe(
        take(1),
        finalize(() => (this.isExportLoading = false))
      )
      .subscribe((response) => {
        const blob = new Blob([response.body]);
        saveBlobAsFile(blob, 'contracts.csv');
      });
  }
}
